import Vue from "vue";
import Vuex from "vuex";
import createVuexAlong from "vuex-along";
import {
  applyCurrentUserState,
  applyLoginState,
  createInitialAuthState,
  resetAuthState,
} from "@/Utils/authState";
import { hasPermission } from "@/Utils/permission";

Vue.use(Vuex);

const PERSISTED_STATE_KEY = "comm-frontend-auth";
let authInitializationPromise = null;

function clearPersistedRuntimeState() {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    const rawState = window.localStorage.getItem(PERSISTED_STATE_KEY);
    if (!rawState) {
      return;
    }

    const persistedState = JSON.parse(rawState);
    const rootState = persistedState && persistedState.root;
    if (!rootState) {
      return;
    }

    [
      "role",
      "afField",
      "region",
      "fieldId",
      "user",
      "permissions",
      "authInitialized",
      "showLoading",
      "loadingRequestCount",
      "notificationQueue",
    ].forEach((key) => delete rootState[key]);
    window.localStorage.setItem(
      PERSISTED_STATE_KEY,
      JSON.stringify(persistedState)
    );
  } catch (error) {
    console.warn("Failed to clear persisted runtime state", error);
  }
}

clearPersistedRuntimeState();

export default new Vuex.Store({
  state: {
    ...createInitialAuthState(),

    // 导航栏信息
    title: [],
    showLoading: false,
    loadingRequestCount: 0,
    notificationQueue: [],

    // 检索数据的条件
    date_range: [], // 时间范围
    selected_devs: "", // 选中的设备
    selected_algoName: undefined, // 选中的数据包算法
    is_insert_empty: false, // 是否插空
    refresh: 0, // 是否刷新页面
    select_bags: undefined, // 是否补包
    fieldFilter: "", // 牧场筛选（数据网站）
    cowBoundFilter: null, // 是否绑定牛只筛选（数据网站）
    milkParlorFieldId: "", // 奶厅运维页面共享的牧场选择
  },
  getters: {
    hasPermission: (state) => (code) => hasPermission(state.permissions, code),
  },
  mutations: {
    pushTitle(state, newTitle) {
      state.title.push(newTitle);
    },
    popTitle(state) {
      state.title.pop();
    },
    changeTitle(state, newTitle) {
      state.title = [newTitle];
    },
    reverseLoading(state) {
      if (state.showLoading) {
        state.loadingRequestCount = 0;
        state.showLoading = false;
      } else {
        state.loadingRequestCount = 1;
        state.showLoading = true;
      }
    },
    setLoading(state, isLoading) {
      const currentCount = Number(state.loadingRequestCount) || 0;
      if (isLoading) {
        state.loadingRequestCount = currentCount + 1;
      } else {
        state.loadingRequestCount = Math.max(currentCount - 1, 0);
      }
      state.showLoading = state.loadingRequestCount > 0;
    },
    resetLoading(state) {
      state.loadingRequestCount = 0;
      state.showLoading = false;
    },
    addNotification(state, notification) {
      state.notificationQueue.push(notification);
    },
    popNotification(state) {
      state.notificationQueue.shift();
    },
    setDateRange(state, date_range) {
      state.date_range = date_range;
    },
    setSelectBags(state, select_bags) {
      state.select_bags = select_bags;
    },
    setFieldFilter(state, fieldFilter) {
      state.fieldFilter = fieldFilter;
    },
    setCowBoundFilter(state, cowBoundFilter) {
      state.cowBoundFilter = cowBoundFilter;
    },
    setMilkParlorFieldId(state, fieldId) {
      state.milkParlorFieldId = fieldId === undefined || fieldId === null ? "" : String(fieldId);
    },
    setSelectedDevs(state, selected_devs) {
      state.selected_devs = selected_devs;
    },
    setSelectedAlgoName(state, selected_algoName) {
      state.selected_algoName = selected_algoName;
    },
    toggleInsertEmpty(state) {
      state.is_insert_empty = !state.is_insert_empty;
    },
    refreshData(state) {
      state.refresh++;
    },
    updateUserInfo(state, newUserInfo) {
      applyLoginState(state, {
        ...newUserInfo,
        identity: newUserInfo.identity || newUserInfo.role,
      });
    },
    loginSucceeded(state, loginData) {
      applyLoginState(state, loginData);
    },
    currentUserSucceeded(state, currentUser) {
      applyCurrentUserState(state, currentUser);
    },
    markAuthInitialized(state) {
      state.authInitialized = true;
    },
    clearAuth(state) {
      resetAuthState(state);
      state.milkParlorFieldId = "";
    },
  },
  actions: {
    pushTitle({ commit }, newTitle) {
      commit("pushTitle", newTitle);
    },
    popTitle({ state, commit }, times) {
      if (state.title.length <= 1) return null;
      let pop = state.title[state.title.length - times];
      while (times-- && pop !== null) {
        commit("popTitle");
      }
    },
    changeTitle({ commit }, newTitle) {
      commit("changeTitle", newTitle);
    },
    reverseLoading({ commit }) {
      commit("reverseLoading");
    },
    setLoading({ commit }, isLoading) {
      commit("setLoading", isLoading);
    },
    resetLoading({ commit }) {
      commit("resetLoading");
    },
    addNotification({ commit }, notification) {
      const routePath =
        notification.routePath ||
        window.location.hash.replace(/^#/, "") ||
        window.location.pathname;
      commit("addNotification", { ...notification, routePath });
    },
    getNotification({ state, commit }) {
      let notification = state.notificationQueue[0];
      commit("popNotification");
      return notification;
    },
    setDateRange({ commit }, date_range) {
      commit("setDateRange", date_range);
    },
    setSelectBags({ commit }, select_bags) {
      commit("setSelectBags", select_bags);
    },
    setFieldFilter({ commit }, fieldFilter) {
      commit("setFieldFilter", fieldFilter);
    },
    setCowBoundFilter({ commit }, cowBoundFilter) {
      commit("setCowBoundFilter", cowBoundFilter);
    },
    setMilkParlorFieldId({ commit }, fieldId) {
      commit("setMilkParlorFieldId", fieldId);
    },
    setSelectedDevs({ commit }, selected_devs) {
      commit("setSelectedDevs", selected_devs);
    },
    setSelectedAlgoName({ commit }, selected_algoName) {
      commit("setSelectedAlgoName", selected_algoName);
    },
    toggleInsertEmpty({ commit }) {
      commit("toggleInsertEmpty");
    },
    refreshData({ commit }) {
      commit("refreshData");
    },
    updateUserInfo({ commit }, newUserInfo) {
      commit("updateUserInfo", newUserInfo);
    },
    loginSucceeded({ commit }, loginData) {
      commit("loginSucceeded", loginData);
    },
    initializeAuth({ state, commit }, options = {}) {
      const force = Boolean(options && options.force);
      if (!state.token) {
        commit("markAuthInitialized");
        return Promise.resolve(null);
      }
      if (state.authInitialized && !force) {
        return Promise.resolve(state.user);
      }
      if (authInitializationPromise) {
        return authInitializationPromise;
      }
      authInitializationPromise = import("@/api/web/User")
        .then(({ default: Auth }) => Auth.getCurrentUser())
        .then((response) => {
          if (response.error) {
            const error = new Error(response.error);
            error.status = response.status;
            throw error;
          }
          commit("currentUserSucceeded", response.data);
          return response.data;
        })
        .finally(() => {
          authInitializationPromise = null;
        });
      return authInitializationPromise;
    },
    clearAuth({ commit }) {
      commit("clearAuth");
    },
  },
  modules: {},
  plugins: [
    createVuexAlong({
      name: "comm-frontend-auth",
      local: {
        // Loading is runtime-only; persisting it can restore a stale full-screen mask after a page close.
        list: [
          "role",
          "afField",
          "region",
          "fieldId",
          "user",
          "permissions",
          "authInitialized",
          "showLoading",
          "loadingRequestCount",
          "notificationQueue",
        ],
        isFilter: true,
      },
    }),
  ],
});
